import {
  ResponsiveContainer,
  ComposedChart,
  XAxis,
  YAxis,
  Bar,
  Line,
  LabelList,
  Tooltip,
  BarChart,
  Legend,
  ReferenceLine,
  Scatter,
  Area,
} from "recharts";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { leakageTimelineOptions2 } from "../constants";
import { numberFormatter } from "../../utils";
import { Button, Divider, Popover, SelectPicker, Stack, Whisper } from "rsuite";
import moment from "moment";
import { OverlayTriggerHandle } from "rsuite/esm/internals/Picker";
import FileDownloadIcon from "@rsuite/icons/FileDownload";
import { CSVLink } from "react-csv";
interface Props {
  chartData: any;
  exportData: any;
  heading: any;
  page: any;
  selectedTimeline: any;
  getTimelineHandler: any;
  isVisible: boolean;
  timelineHandler?: (args: { baseline: number; current: number }) => void;
  timelineObject?: { baseline: number; current: number };
}
interface T {
  quarter: string;
  actual: number;
  forecastedSpend: number;
  spendNeededForNextTier: number;
  spendShift: number;
  targetGrowthSpend: number;
}

const CustomLabel = ({ payload, page, data }: any) => {
  const { x, y, value, index } = payload;
  const offset = 20;
  // Find the last index where gm is not null
  let lastGmIndex = -1;
  for (let i = data.length - 1; i >= 0; i--) {
    if (data[i].gm !== null) {
      lastGmIndex = i;
      break;
    }
  }
  const showLabel = index === 0 || index === lastGmIndex;
  return (
    <g>
      {showLabel && (
        <>
          <line
            x1={x}
            y1={y}
            x2={x}
            y2={y + offset}
            stroke="gray"
            opacity={0.5}
          />
          <text
            x={x - offset + 8}
            y={y + offset + 10}
            fill="gray"
            fontSize={`${page == "summary" ? "1.4vh" : "1.3vh"}`}
            opacity={`${page == "summary" ? 0.8 : 1}`}
          >
            {numberFormatter(value, 1) + "%"}
          </text>
        </>
      )}
    </g>
  );
};
const CustomLabel2 = ({ payload, page, data }: any) => {
  const { x, y, value, index } = payload;
  const offset = 20;
  // Find the first and last indices where gmCur is not null
  let firstGmCurIndex = -1;
  let lastGmCurIndex = -1;
  for (let i = 0; i < data.length; i++) {
    if (data[i].gmCur !== null) {
      firstGmCurIndex = i;
      break;
    }
  }
  for (let i = data.length - 1; i >= 0; i--) {
    if (data[i].gmCur !== null) {
      lastGmCurIndex = i;
      break;
    }
  }
  const showLabel =
    (firstGmCurIndex !== -1 && index === firstGmCurIndex) ||
    index === lastGmCurIndex;
  return (
    <g>
      {showLabel && (
        <>
          <line
            x1={x}
            y1={y}
            x2={x}
            y2={y - offset - 10}
            stroke="#071158"
            opacity={0.5}
          />
          <text
            x={x - offset + 5}
            y={y - offset - 15}
            fill="#012169"
            fontSize={`${page == "summary" ? "1.4vh" : "1.3vh"}`}
            opacity={`${page == "summary" ? 0.8 : 1}`}
          >
            {numberFormatter(value, 1) + "%"}
          </text>
        </>
      )}
    </g>
  );
};

type Year = 2024 | 2025 | 2026;

const MarginImprovement = (props: Props) => {
  let data: any = props?.chartData;
  const { page, exportData } = props;
  const [first4SumGM, setFirst4SumGM] = useState<any>(0);
  const [restSumGM, setRestSumGM] = useState<any>(0);
  const [newData, setNewData] = useState<any>([]);

  console.log(data, exportData, "export");

  const [selectedTimeline, setSelectedTimeline] = useState<any>(
    leakageTimelineOptions2[0].value,
  );

  const whisperRef = useRef<OverlayTriggerHandle>(null);
  const popoverBodyRef = useRef<HTMLDivElement | null>(null);

  const baselineData = useMemo(
    () => [2024, 2025].map((y) => ({ label: String(y), value: y as Year })),
    [],
  );
  const currentData = useMemo(
    () => [2025, 2026].map((y) => ({ label: String(y), value: y as Year })),
    [],
  );

  const [baseline, setBaseline] = useState<Year | null>(null);
  const [current, setCurrent] = useState<Year | null>(null);

  const [timelineObject, setTimelineObject] = useState<{
    baseline: number;
    current: number;
  }>({ baseline: 2025, current: 2026 });

  const ready = baseline != null && current != null;
  const close = () => whisperRef.current?.close();
  const label = ready
    ? `Baseline ${baseline} • Current ${current}`
    : "Select years";

  const exportFullRef = useRef<any>(null);
  const [export1Data, setExport1Data] = useState<any>([]);

  const csvHeaders = [
    { label: "Week Start Month", key: "mwstartDate" },
    { label: "Revenue", key: "revenue" },
    { label: "Cost", key: "cost" },
    { label: "GM", key: "gm" },
    { label: "GM wo Leakage", key: "gmWoLeakage" },
    { label: "Cost Reduction", key: "avgCostPrev_minus_costCurr" },
    { label: "Leakage", key: "leakage" },
  ];

  const mergeByWeekDelta = (arr1: any, arr2: any) => {
    const toDayKey = (d: any) => new Date(d).toISOString().slice(0, 10); // YYYY-MM-DD

    // index 2nd array by date, keep only the delta you want
    const deltaByDay = new Map(
      arr2.map((r: any) => {
        const delta = (r.total_avg_cost_Prev ?? 0) - (r.total_cost_Curr ?? 0);
        return [toDayKey(r.week), delta];
      }),
    );

    // merge into the 1st array
    return arr1?.map((r: any) => {
      const dayKey = toDayKey(r.mwstartDate);
      const delta = deltaByDay.get(dayKey); // undefined if no match
      return { ...r, avgCostPrev_minus_costCurr: delta };
    });
  };

  useEffect(() => {
	setExport1Data(mergeByWeekDelta(data?.graph1,exportData?.finalData))
  }, [exportData, data?.graph1]);

  console.log(export1Data?.map((val:any)=>{console.log(val)}),"export1Daata")

  const overlay = (
    <Popover title="Select years" style={{ width: 320 }}>
      <div ref={popoverBodyRef}>
        <Stack direction="column" spacing={10} alignItems="stretch">
          <div>
            <div style={{ fontSize: 12, marginBottom: 6 }}>
              Baseline (required)
            </div>
            <SelectPicker
              data={baselineData}
              value={baseline}
              onChange={setBaseline}
              placeholder="Select baseline year"
              searchable={false}
              block
              container={() => popoverBodyRef.current as HTMLElement}
            />
          </div>

          <div>
            <div style={{ fontSize: 12, marginBottom: 6 }}>
              Current (required)
            </div>
            <SelectPicker
              data={currentData}
              value={current}
              onChange={setCurrent}
              placeholder="Select current year"
              searchable={false}
              block
              container={() => popoverBodyRef.current as HTMLElement}
            />
          </div>

          <Divider style={{ margin: "6px 0" }} />
        </Stack>
      </div>
    </Popover>
  );

  useEffect(() => {
    let newDataSet: any[] = [];
    let first4SumGM1 = 0;
    let restSumGM1 = 0;
    // First 4 items as-is
    data?.graph1?.slice(0, 4).forEach((item: any) => {
      const gmValue = item.gm * 100;
      first4SumGM1 += gmValue;
      newDataSet.push({
        date: moment.utc(item.mwstartDate).format("MMM"),
        gm: item.gm * 100,
        gmCur: null,
        gmleak: item.gmWoLeakage ? item.gmWoLeakage * 100 : null,
        master_vendor_name:
          item.master_vendor_name === "BRIDGEPORT FITTINGS, INC."
            ? "BRIDGEPORT"
            : item.master_vendor_name || null,
        dotValue: item.master_vendor_name
          ? item.gm * 100 > 25
            ? item.gm * 100 - 5
            : item.gm * 100
          : null,
      });
    });

    const restItems = data?.graph1?.slice(4);

    // Find last two months in restItems
    const months = restItems?.map((item: any) =>
      moment.utc(item.mwstartDate).format("YYYY-MM"),
    );
    const uniqueMonths = Array.from(new Set(months));
    const lastTwoMonths = uniqueMonths.slice(-2);

    // Separate items: earlier months and last two months
    const earlierMonthGroups: { [key: string]: any[] } = {};
    const lastTwoMonthWeeks: any[] = [];

    restItems?.forEach((item: any) => {
      const gmValue = item.gm * 100;
      restSumGM1 += gmValue;
      const monthKey = moment.utc(item.mwstartDate).format("YYYY-MM");
      if (lastTwoMonths.includes(monthKey)) {
        // Collect weekly data for last two months
        lastTwoMonthWeeks.push({
          date: moment.utc(item.mwstartDate).format("MMM DD"),
          gm: null,
          gmCur: item.gm * 100,
          gmleak: item.gmWoLeakage ? item.gmWoLeakage * 100 : null,
          master_vendor_name:
            item.master_vendor_name === "BRIDGEPORT FITTINGS, INC."
              ? "BRIDGEPORT"
              : item.master_vendor_name || null,
          dotValue: item.master_vendor_name
            ? item.gm * 100 > 25
              ? item.gm * 100 - 5
              : item.gm * 100
            : null,
        });
      } else {
        // Group by month for earlier months
        if (!earlierMonthGroups[monthKey]) {
          earlierMonthGroups[monthKey] = [];
        }
        earlierMonthGroups[monthKey].push(item);
      }
    });

    // Aggregate grouped months and push merged data
    Object.entries(earlierMonthGroups).forEach(
      ([monthKey, items], index: any) => {
        console.log(items, "items");
        const totalRev = items.reduce(
          (sum: number, i: any) => sum + i.revenue || 0,
          0,
        );
        const totalCost = items.reduce(
          (sum: number, i: any) => sum + i.cost || 0,
          0,
        );
        const totalLeakage = items.reduce(
          (sum: number, i: any) => sum + i.leakage || 0,
          0,
        );

        newDataSet.push({
          date: moment(monthKey, "YYYY-MM").format(`MMM'YY`),
          gm: index == 0 ? ((totalRev - totalCost) / totalRev) * 100 : null,
          gmCur: ((totalRev - totalCost) / totalRev) * 100,
          gmleak:
            !newDataSet[newDataSet?.length - 1]?.gmleak && totalLeakage === 0
              ? null
              : ((totalRev - totalCost + totalLeakage) / totalRev) * 100,
          master_vendor_name: null,
          dotValue: null,
        });
      },
    );

    if (Object.entries(earlierMonthGroups)?.length === 0) {
      lastTwoMonthWeeks[0].gm = lastTwoMonthWeeks[0].gmCur;
    }

	if(Object.entries(earlierMonthGroups)?.length===0){
			lastTwoMonthWeeks[0].gm=lastTwoMonthWeeks[0].gmCur
	}

    // Finally, append the last two months' weekly data
    newDataSet = [...newDataSet, ...lastTwoMonthWeeks];
    console.log(first4SumGM1, restSumGM1, 123);
    setNewData(newDataSet);
    setFirst4SumGM(first4SumGM1);
    setRestSumGM(restSumGM1);
  }, [data]);

  // Generate the ticks array for the XAxis
  const ticks = [];
  if (newData && newData.length > 0) {
    // Add all of the first 4 items (monthly data)
    for (let i = 0; i < Math.min(4, newData.length); i += 2) {
      ticks.push(newData[i].date);
    }

    // For the remaining items (daily data), add every third element starting from index 4
    for (let i = 4; i < newData.length; i += 3) {
      ticks.push(newData[i].date);
    }
  }
  let newData1: any = [];

  if (newData?.length > 0) {
    // Process each data point to add the range information
    newData1 = newData
      .map((item: any, id: any) => {
        const newItem = { ...item };

        // Determine which value is larger for this point
        if (item.gmleak > item.gmCur) {
          // When 2024 cost is higher than 2025: green area
          newItem.greenRange = [item.gmCur, item.gmleak];
          newItem.redRange = [item.gmCur, item.gmCur]; // zero width
        } else if (item.gmCur > item.gmleak) {
          // When 2025 cost is higher than 2024: red area
          newItem.greenRange = [item.gmCur, item.gmCur]; // zero width
          newItem.redRange = [item.gmleak, item.gmCur];
        } else {
          // When costs are equal: no area
          newItem.greenRange = [item.gmCur, item.gmCur];
          newItem.redRange = [item.gmCur, item.gmCur];
        }
        return newItem;
      })
      .filter(Boolean); // Filter out any null entries from non-covered months
  }

  let heading = props?.heading;
  const customTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;

      const popupStyle = {
        width: "fit-content",
        background: "white",
        border: "1px solid #ccc",
        boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
        borderRadius: "1vh",
        color: "black",
        padding: 0,
      };

      // Determine header label and value
      const isWeek = data?.date?.split(" ")?.length > 1;
      const headerLabel = isWeek ? "Week" : "Month";
      const headerValue = data.date;

      return (
        <div style={popupStyle}>
          {/* Header Row */}
          <div className="w-full flex justify-start h-[3vh] items-center bg-[#f0f4f8] py-[3vh] px-[1vh] textSmall">
            <div className="font-bold uppercase">{headerLabel}:</div>
            <div className="font-bold ml-1" style={{ color: "#3f3f3f" }}>
              {headerValue}
            </div>
          </div>

          {/* Data Rows */}
          <div className="h-fit p-[1vh] mt-[.5vh] mb-[.5vh]">
            <p className="label" style={{ padding: 0, margin: 0 }}>
              <label
                className="graph-label-small"
                style={{
                  color: "#5a5a5a",
                  fontWeight: "normal",
                }}
              >
                {data.gmCur
                  ? "Gross Margin'25 (%) :"
                  : "Gross Margin'24 (%) :"}{" "}
              </label>
              <label
                className="graph-label-small"
                style={{ color: "#3f3f3f", fontWeight: "bold" }}
              >
                {numberFormatter(data.gmCur ?? data.gm, 2)}%
              </label>
            </p>
            {data.gmleak && (
              <p className="label" style={{ padding: 0, margin: 0 }}>
                <label
                  className="graph-label-small"
                  style={{
                    color: "#5a5a5a",
                    fontWeight: "normal",
                  }}
                >
                  Gross Margin'25 wo Leakage (%) :
                </label>
                <label
                  className="graph-label-small"
                  style={{
                    color: "#3f3f3f",
                    fontWeight: "bold",
                  }}
                >
                  {numberFormatter(data.gmleak, 2)}%
                </label>
              </p>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  const timelineHandler = (e: any) => {
    setSelectedTimeline(e === null ? "All" : e);
  };

  const getReferenceValue = (line: any) => {
    let reference: number = 0;
    if (line == "lastYear") {
      if (selectedTimeline == "preGoLive") {
        reference = data?.calculationData?.lastyearGM * 100;
      } else if (selectedTimeline == "last1Months") {
        reference =
          data?.calculationData?.timelineDistribution.last1Months.gm * 100;
      } else if (selectedTimeline == "last3Months") {
        reference =
          data?.calculationData?.timelineDistribution.last3Months.gm * 100;
      } else if (selectedTimeline == "last6Months") {
        reference =
          data?.calculationData?.timelineDistribution.last6Months.gm * 100;
      } else if (selectedTimeline == "last12Months") {
        reference =
          data?.calculationData?.timelineDistribution.last12Months.gm * 100;
      }
    } else if (line == "currYear") {
      if (selectedTimeline == "preGoLive") {
        reference = data?.calculationData?.curryearGM * 100;
      } else {
        reference =
          data?.calculationData?.timelineDistribution.last4Weeks.gm * 100;
      }
    }
    return reference;
  };

  const exportDataClick = () => {
    if (export1Data?.length > 0) {
      exportFullRef?.current?.link.click();
    }
  };

  return (
    <div className={`w-full graphBox h-[100%]`}>
      <div className="headSpace">
        <span className="headContent">{heading} </span>
        <div className="filter-flex border-1 max-w-fit">
          <div className="flex justify-center items-center h-full">
            <Button
              className="bg-transparent hover:bg-transparent"
              onClick={exportDataClick}
            >
              <FileDownloadIcon color="black" className="text-fsre16" />
            </Button>
          </div>
          <CSVLink
            ref={exportFullRef}
            data={export1Data}
            headers={csvHeaders}
            filename="margin_improvement.csv"
            className="hidden"
            target="_blank"
          >
            {" "}
          </CSVLink>
        </div>
        <div className="flex flex-col justify-end">
          <p
            className={`flex justify-end px-[2vh] font-semibold text-[1.5vh] mt-[0] ${getReferenceValue("currYear") - getReferenceValue("lastYear") > 0 ? "text-[#40a108]" : "text-[#a10808]"} `}
          >
            {numberFormatter(
              (getReferenceValue("currYear") - getReferenceValue("lastYear")) *
                100,
              1,
            )}
            bps Margin Improvement
          </p>
          <p className="flex justify-end px-[2vh] font-semibold text-[1.5vh] mt-[0] text-[#a10808]">
            Latest Week Margin Leakage:
            {newData1[newData1?.length - 1]?.gmleak -
              newData1[newData1?.length - 1]?.gmCur <=
            0
              ? " No Leakage"
              : ` ${numberFormatter((newData1[newData1?.length - 1]?.gmleak - newData1[newData1?.length - 1]?.gmCur) * 100, 1)} bps`}
          </p>
        </div>
      </div>
      <div className="sub-heading-text font-bold flex flex-col items-start">
        <span className="filterHead">Timeline</span>
        <Whisper
          trigger="click"
          placement="bottomStart"
          speaker={overlay}
          onClose={() => {
            if (baseline && current) {
              //   setIsGraphLoading(true);
              //   setIsGraph2Loading(true);
              //   getGraphData();
              //   getGraphDataSet2();
              setTimelineObject({ baseline: baseline, current: current });
              props.timelineHandler?.({ baseline, current });
            }
          }}
        >
          <Button className="timeline_button" appearance="default">
            {label}
          </Button>
        </Whisper>
      </div>

      <ResponsiveContainer
        width="100%"
        height="75%"
        className="bg-[#ffff] px-[3.5vh] pt-[2vh]"
      >
        <ComposedChart
          data={newData1}
          margin={{
            top: 10,
            right: 40,
            left: 40,
          }}
        >
          <XAxis
            dataKey="date"
            className="graph-label-small "
            ticks={ticks}
            stroke="black"
          />
          <YAxis
            hide
            fontSize="1.5vh"
            domain={[
              (dataMin: any) => dataMin / 1.2,
              (dataMax: any) => dataMax * 1.1,
            ]}
            tickFormatter={(value: number) => numberFormatter(value, 0, "$")}
          />
          <Tooltip
            contentStyle={{
              color: "white",
              backgroundColor: "rgb(26, 25, 22)",
            }}
            cursor={{ strokeDasharray: "3 3", fill: "#212121" }}
            content={customTooltip}
          />

          {first4SumGM > 0 && (
            <ReferenceLine
              y={data?.calculationData?.lastyearGM * 100}
              label={{
                position: "insideTopRight",
                fill: "gray",
                fontStyle: "italic",
                fontSize: "1.8vh",
                value:
                  numberFormatter(data?.calculationData?.lastyearGM * 100, 2) +
                  "%",
              }}
              stroke="gray"
              strokeDasharray="5 3"
              opacity={1}
            />
          )}
          {restSumGM > 0 && (
            <ReferenceLine
              y={data?.calculationData?.curryearGM * 100}
              label={{
                position: "insideBottomLeft",
                fill: "#012169",
                fontStyle: "italic",
                fontSize: "1.8vh",
                value:
                  numberFormatter(data?.calculationData?.curryearGM * 100, 2) +
                  "%",
              }}
              stroke="#012169"
              strokeDasharray="5 3"
              opacity={1}
            />
          )}
          {/* Green area for when 2024 is higher */}
          <Area
            dataKey="redRange"
            stroke="#dce0a7"
            fill="#dce0a7"
            isAnimationActive={false}
          />
          {/* Red area for when 2025 is higher */}
          <Area
            dataKey="greenRange"
            stroke="#ffcccc"
            fill="#ffcccc"
            isAnimationActive={false}
          />
          <Line
            type="linear"
            dataKey="gm"
            stroke="gray"
            name={`${timelineObject?.baseline || 2024}`}
            strokeDasharray={"0"}
            connectNulls={true}
            strokeWidth={2}
            // dot={page == 'summary' ? true : false}
            dot={
              page == "summary"
                ? ($) => <CustomDot {...$} data={newData1} color={"gray"} />
                : false
            }
          >
            <LabelList
              dataKey="gm"
              content={(props) => (
                <CustomLabel payload={props} page={page} data={newData1} />
              )}
            />
          </Line>
          <Line
            type="linear"
            dataKey="gmCur"
            stroke="#012169"
            name={`${timelineObject?.current || 2025}`}
            strokeDasharray={"0"}
            connectNulls={true}
            strokeWidth={2}
            // dot={page == 'summary' ? true : false}
            dot={
              page == "summary"
                ? ($) => <CustomDot {...$} data={newData1} color={"#012169"} />
                : false
            }
          >
            <LabelList
              dataKey="gmCur"
              content={(props) => (
                <CustomLabel2 payload={props} page={page} data={newData1} />
              )}
            />
          </Line>
          <Line
            type="linear"
            dataKey="gmleak"
            stroke="#db210dff"
            name={`${timelineObject?.current || 2025} leak`}
            strokeDasharray={"10 10"}
            connectNulls={true}
            strokeWidth={3}
            // dot={page == 'summary' ? true : false}
            dot={false}
          >
            {/* <LabelList dataKey="gmleak" content={(props) => <CustomLabel2 payload={props} page={page} data={newData} />} /> */}
          </Line>
          {page == "summary" && (
            <Scatter dataKey="dotValue" fill="none" legendType="none">
              <LabelList
                dataKey="master_vendor_name"
                fill="black"
                fontWeight={700}
                fontSize="1.2vh"
                formatter={(value: number) => `${value} Go-Live`}
                position="bottom"
              />
            </Scatter>
          )}
        </ComposedChart>
      </ResponsiveContainer>
      <div className="contentFooter !justify-end">
        <CustomLegend baseline={timelineObject?.baseline || 2024} current={timelineObject?.current || 2025}></CustomLegend>
      </div>
    </div>
  );
};
const CustomLegend = ({ baseline, current }: any) => (
  <div
    style={{
      display: "flex",
      gap: "2rem",
      alignItems: "center",
      color: "black",
      fontSize: "0.8rem",
    }}
  >
    <span>
      <span
        style={{
          display: "inline-block",
          width: 10,
          height: 18,
          background: "gray",
          marginRight: 8,
          verticalAlign: "middle",
        }}
      />
      {baseline}
    </span>
    <span>
      <span
        style={{
          display: "inline-block",
          width: 10,
          height: 18,
          background: "#012169",
          marginRight: 8,
          verticalAlign: "middle",
        }}
      />
      {current}
    </span>
  </div>
);
const CustomDot = (props: any) => {
  const { cx, cy, index, data, color } = props;
  // Only render for first and last point
  if (index === 0 || index === data.length - 1) {
    return color == "gray" && index == 0 ? (
      <circle cx={cx} cy={cy} r={5} fill={color} stroke="none" />
    ) : color == "#012169" && index !== 0 ? (
      <circle cx={cx} cy={cy} r={5} fill={color} stroke="none" />
    ) : (
      <></>
    );
  }
  return null;
};
export default MarginImprovement;
